// The table pieces the Forecasting views share (2026-10-27, moved out of
// ForecastPhoneChat.jsx when the Email view needed them): a bordered cell that a tone
// can tint, a header cell, the week cell with its tags, an FTE figure with its hours,
// a full-width divider row, and this week's row highlight.
import { Typography, TableCell, TableRow } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { formatFte, formatHoursShort, weekLabel, weekTags, EM_DASH } from '../forecastPhoneChat.js'

export const BORDER = { border: '1px solid', borderColor: 'divider' }
export const GROUP_LEFT = { borderLeft: '2px solid', borderLeftColor: 'text.disabled' }

// One table cell: a tone tints it, `left` starts a column group, `muted` marks
// "no data", `italic` marks a projection.
export function Cell({ children, tone = null, bold = false, left = false, muted = false, italic = false, align = 'right', colSpan }) {
  return (
    <TableCell
      align={align}
      colSpan={colSpan}
      sx={{
        ...BORDER,
        ...(left ? GROUP_LEFT : {}),
        py: 0.5,
        px: 1,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        fontWeight: bold || tone === 'error' ? 600 : undefined,
        fontStyle: italic ? 'italic' : undefined,
        color: muted ? 'text.secondary' : undefined,
        bgcolor: tone ? (t) => alpha(t.palette[tone].main, t.palette.mode === 'dark' ? 0.28 : 0.16) : undefined,
      }}
    >
      {children}
    </TableCell>
  )
}

export function Head({ children, colSpan, rowSpan, left = false, align = 'center' }) {
  return (
    <TableCell
      align={align}
      colSpan={colSpan}
      rowSpan={rowSpan}
      sx={{ ...BORDER, ...(left ? GROUP_LEFT : {}), py: 0.5, px: 1, fontWeight: 600, whiteSpace: 'nowrap', bgcolor: 'action.hover' }}
    >
      {children}
    </TableCell>
  )
}

export function WeekCell({ row }) {
  const tags = weekTags(row)
  return (
    <TableCell sx={{ ...BORDER, py: 0.5, px: 1, whiteSpace: 'nowrap', fontWeight: 600 }}>
      {weekLabel(row.week)}
      {tags.map((tag) => (
        <Typography key={tag} component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, fontWeight: 400 }}>{tag}</Typography>
      ))}
    </TableCell>
  )
}

// FTE headline with the hours under it when the toggle is on.
export function Fte({ value, showHours }) {
  if (!value) return <>{EM_DASH}</>
  const hours = showHours ? formatHoursShort(value.hours) : null
  return (
    <>
      {formatFte(value.fte)}
      {hours && <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>{hours}</Typography>}
    </>
  )
}

export function Divider({ colSpan, children }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} sx={{ ...BORDER, py: 0.75, px: 1, fontWeight: 700, bgcolor: 'action.selected', letterSpacing: '0.02em' }}>
        {children}
      </TableCell>
    </TableRow>
  )
}

export function rowSx(row) {
  return row.isThisWeek ? { bgcolor: (t) => alpha(t.palette.primary.main, 0.08) } : undefined
}
